/**
 * @module geo/types
 */


import {MapEvents} from '../events/map.events';

import {BaseLayer} from './base.layer';
import {Coordinates} from './coordinates';
import {ExtentA, ExtentOptions} from './extent'
import {MapEventNotify} from './map.event.notify';
import {IHashEntry, MapEntry} from './map/layer/map.entry';
import {IHashMarker} from './map/marker';

/**
 * This type contains properties for  [[map]] class.
 *  Properties can be set at initialization, like position,
 * or extent, or entries.
 *
 * For instance :
 *
 *
 * to set an extent on map init :
 *
 *
 *
 * ```javascript
 * var view =
 * {
 *   map: {
 *    "id": "geomap",
 *    "extent":{
 *       minX: 61,
 *       maxX: 23,
 *       minY: 2312,
 *       maxY: 12,
 *       crs: 2154,
 *    },
 * }
 * // or to set the center and zoom level :
 *
 * var view =
 * {
 *      map:
 *
 *        {
 *         Position: {
 *              coordinates: [3, 47],
 *              crs: "4326"
 *          },
 *          scaleDenominator: 25000
 *
 *        }
 *
 * }
 * window.geoApp = geoApi.application("map",
 * "http://test.bg.lan/adws/app/3940ce6a-2924-11e8-8ead-afd02070f4a9/", view)
 *
 *
 * ```
 *
 * Entries and analyses visibilities can also be set this way :
 *
 * ```javascript
 * var view= {
 *
 *  map:{
 *       entries:[
 *          {
 *              name: "rail",
 *              visible: false
 *           },
 *           {
 *                  name: "routes",
 *                  visible: true,
 *                  subEntriesIndexes: [1],
 *                  analyses: [
 *                               {
 *                                 name: "road type",
 *                                visible: true
 *                             }
 *                           ]
 *            }
 *
 *       ]
 *   }
 * }
 * ```
 *
 * For live examples, see samples
 *
 */
export interface MapOptions {
  /**
   * Specify an id of map, if none, one will be setted
   */
  id?: string;
  /**
   * The extent represents the visible portion of a map within the view as an
   * instance of Extent.
   */
  extent?: ExtentOptions|undefined;
  /**
   * Represents the view's center point.
   */
  position?: Coordinates|undefined;
  /**
   * Zoom in meters at initialization
   */
  zoom?: number|undefined;
  /**
   *
   * Scale reference
   * @private
   *
   */
  scale?: number|undefined;
  /**
   * The scale denominator, for instance, if map it as 1/20000 , it will be set
   * at 20000
   */
  scaleDenominator?: number|undefined;
  /**
   * [[MapEvents]] triggered by map
   */
  listenEvents?: MapEvents[];
  /**
   * A collection containing a hierarchical list of all the created entries of
   * the operational layers in the map.
   */
  entries?: IHashEntry;
  /**
   * A collection containing a hierarchical list of all the created base layers
   * ( [[BaseLayer]] ) of the operational layers in the map.
   */
  baseLayers?: BaseLayer[];
  /**
   * A reference to the last position when the pointer moved. Can't be setted at
   * initialisation
   */
  pointerMove?: MapEventNotify;
  /**
   * A reference to the last position when the map was clicked. Can't be setted
   * at initialisation
   */
  pointerClick?: MapEventNotify;
  /**
   * list of all [[Markers]] on the map
   */
  markers?: IHashMarker|undefined;
  /**
   * map view crs
   */
  crs?: string;

  /**
   * when map has been initialized
   */
  initialized?:boolean;
}

/**
 * @private
 */
export abstract class MapStore implements MapOptions {
  id?: string;
  _extent?: ExtentA;
  markers: IHashMarker = {};

  public get extent(): ExtentA|undefined {
    return this._extent;
  }

  public set extent(extent: ExtentA|undefined) {
    this._extent = <ExtentA>extent;
  }
}
